<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@stores';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import EditForm from './components/EditForm.vue';

const apiUrl = import.meta.env.VITE_API_URL;
const { t } = useI18n({ useScope: 'global' });
const authStore = useAuthStore();
const toast = useToast();
const confirm = useConfirm();
const apikeys = ref('');
const isDialogVisible = ref(false);
const selectedApiKeyId = ref(null);
const apikeysData = ref([]);

const resolver = () => {
    const errors = {};
    if (!apikeys.value) {
        errors.name = [{ message: t('apikey.keyRequired') }];
    }
    return {
        errors,
    };
};

const getAPIKeyData = async () => {
    try {
        const data = await axios.get(`${apiUrl}/v1/keys`, {
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });

        apikeysData.value = data.data.data;
    } catch (error) {
        console.error(error);
    }
};

const confirmDelete = ({ id, name }) => {
    confirm.require({
        group: 'templating',
        message: t('apikey.deleteMessage', { name }),
        header: t('apikey.deleteTitle'),
        icon: 'pi pi-info-circle',
        rejectLabel: t('form.cancel'),
        rejectProps: {
            label: t('form.cancel'),
            severity: 'secondary',
            outlined: true,
        },
        acceptProps: {
            label: t('form.delete'),
            severity: 'danger',
        },
        accept: async () => {
            try {
                await axios.delete(`${apiUrl}/v1/key`, {
                    params: {
                        id,
                    },
                    headers: {
                        Authorization: `Bearer ${authStore.userToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                const index = apikeysData.value.findIndex((item) => item.id === id);
                if (index !== -1) {
                    apikeysData.value.splice(index, 1);
                }

                toast.add({ severity: 'success', summary: t('apikey.deleteTitle'), detail: t('apikey.deleteKeySuccess'), life: 3000 });
            } catch (error) {
                toast.add({ severity: 'error', summary: t('apikey.deleteTitle'), detail: error, life: 3000 });
            }
        },
        reject: () => {},
    });
};

const createAPIKey = async ({ valid, reset }) => {
    if (!valid) return;

    try {
        const response = await axios.post(
            `${apiUrl}/v1/key`,
            {
                name: apikeys.value,
            },
            {
                headers: {
                    Authorization: `Bearer ${authStore.userToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        apikeys.value = '';
        reset();
        apikeysData.value.unshift(response.data.data);
        if (valid) {
            toast.add({
                severity: 'success',
                summary: t('apikey.createAPI'),
                detail: t('apikey.createKeySuccess'),
                life: 3000,
            });
        }
    } catch (error) {
        console.error(error);
    }
};

const toggleStatus = (data) => {
    confirm.require({
        group: 'templating',
        message: t('apikey.switchMessage', { name: data.is_active ? t('apikey.deactivate') : t('apikey.activate') }),
        header: t('apikey.switchTitle'),
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: t('global.close'),
        rejectProps: {
            label: t('global.close'),
            severity: 'secondary',
            outlined: true,
        },
        acceptProps: {
            label: t('global.save'),
        },
        accept: async () => {
            try {
                await axios.post(
                    `${apiUrl}/v1/key`,
                    {
                        name: data.name,
                        id: data.id,
                        is_active: data.is_active,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${authStore.userToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                toast.add({
                    severity: 'success',
                    summary: t('apikey.switchTitle'),
                    detail: t('apikey.switchSuccess', { name: data.is_active ? t('apikey.activate') : t('apikey.deactivate') }),
                    life: 3000,
                });
            } catch (error) {
                toast.add({ severity: 'error', summary: t('apikey.deleteTitle'), detail: error, life: 3000 });
            }
        },
        reject: () => {
            data.is_active = !data.is_active;
        },
    });
};

const openEdit = (id) => {
    isDialogVisible.value = true;
    selectedApiKeyId.value = id;
};

const saveEdit = (updatedData) => {
    const key = apikeysData.value.find((key) => key.id === updatedData.id);
    if (key) {
        key.name = updatedData.name;
    }
    toast.add({
        severity: 'success',
        summary: t('apikey.editKey'),
        detail: t('apikey.editKeySuccess'),
        life: 3000,
    });
};

const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        toast.add({ severity: 'success', summary: t('global.copied'), life: 3000 });
    });
};

onMounted(() => {
    getAPIKeyData();
});
</script>

<template>
    <div class="card">
        <DataTable :value="apikeysData" class="w-full">
            <template #empty> {{ t('global.noData') }}</template>
            <Column field="key" :header="t('apikey.key')">
                <template #body="{ data }">
                    <div class="flex items-center gap-1">
                        <code>{{ data.key }}</code>
                    </div>
                </template>
            </Column>
            <Column field="name" :header="t('apikey.name')"> </Column>
            <Column field="is_active" :header="t('global.status')">
                <template #body="{ data }">
                    <ToggleSwitch v-model="data.is_active" @click="toggleStatus(data)" />
                </template>
            </Column>
            <Column :header="t('global.actions')">
                <template #body="{ data }">
                    <div class="table-actions">
                        <Button icon="pi pi-copy" variant="outlined" @click="copyToClipboard(data.key)" />

                        <Button icon="pi pi-pen-to-square" severity="info" variant="outlined" :aria-label="t('form.edit')" @click="openEdit(data.id)" />

                        <Button icon="pi pi-trash" severity="danger" variant="outlined" @click="confirmDelete(data)" />
                    </div>
                </template>
            </Column>
        </DataTable>

        <Toast />

        <Form v-slot="$form" :resolver="resolver" @submit="createAPIKey" class="generate-container">
            <label class="input-label">{{ t('apikey.createAPI') }}</label>
            <div class="input-wrapper">
                <div class="flex flex-col gap-1">
                    <InputText name="name" type="text" :placeholder="t('apikey.keyName')" v-model.trim="apikeys" />
                    <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">{{ $form.name.error?.message }}</Message>
                </div>
                <Button :label="t('apikey.generate')" type="submit" />
            </div>
        </Form>
    </div>
    <ConfirmDialog group="templating" :closable="false">
        <template #message="slotProps">
            <div class="flex items-center w-full gap-4">
                <i :class="slotProps.message.icon" class="!text-4xl"></i>
                <p>{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>

    <EditForm v-model:visible="isDialogVisible" v-if="isDialogVisible" :apikeyId="selectedApiKeyId" @update:isVisible="isDialogVisible = $event" @save="saveEdit" />
</template>

<style scoped>
.card {
    @apply bg-white p-4 flex gap-4;
}
.generate-container {
    @apply flex flex-col gap-2;
}
.input-wrapper {
    @apply flex items-start gap-2 py-2;
}
.input-label {
    @apply text-lg font-semibold border-b p-[8.5px];
}
.table-actions {
    @apply flex gap-2 items-center;
}
</style>
